import { FC, useMemo } from 'react';
import { max } from 'd3';

import {
  Accessor,
  Bars,
  CategoryAxis,
  categoryScale,
  LinearAxis,
  linearScale,
  Link,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { ModelQueryHistoryStatus } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useModelQueryUsage } from '@/hooks/discovery/performance/modelQueryUsage';
import { getISODateUTCArrayFromDateStrings } from '@/util/date';
import { ChartWrapper } from './ChartWrapper';

export const QUERY_USAGE_TOOLTIP_NO_DATA =
  'This is enabled but gathering usage consumption queries may take up to 24 hours to display.';

const QUERY_USAGE_TOOLTIP_ERROR = (
  <span>
    An error occurred and the data was not able to load. Learn how to troubleshoot
    permissions{' '}
    <Link
      isInternal={false}
      to="https://docs.getdbt.com/docs/collaborate/model-query-history#credential-permissions"
      onClick={(e) => e.stopPropagation()}
      shouldOpenNewTab
    >
      here
    </Link>
    .
  </span>
);
export interface MostQueryUsageBarChartProps {
  environmentId: number;
  startDate: string;
  endDate: string;
  uniqueId: string;
  setOldestDate: (date: Date | undefined) => void;
}

export type QueryDataPoint = { date: string; value: number };

export const ModelQueryUsageBarChartImpl: FC<MostQueryUsageBarChartProps> = ({
  environmentId,
  startDate,
  endDate,
  uniqueId,
  setOldestDate,
}) => {
  const [queryUsage] = useModelQueryUsage({
    environmentId: environmentId,
    startDate: startDate,
    endDate: endDate,
    uniqueId: uniqueId,
  });

  const {
    chartData,
    totalCount,
  }: { chartData?: QueryDataPoint[]; totalCount?: number } = useMemo(() => {
    const modelQueryUsage = queryUsage.data?.performance.resourceQueryHistory;
    const maybeOldestDate = queryUsage.data?.performance.oldestQueryHistoryDate;
    setOldestDate(maybeOldestDate ? new Date(maybeOldestDate) : undefined);
    if (!modelQueryUsage) {
      return {};
    }

    if (modelQueryUsage.every((x) => x.totalCount === 0)) {
      return { chartData: [], totalCount: 0 };
    }
    const dateArray = getISODateUTCArrayFromDateStrings(startDate, endDate);

    const dayMap = modelQueryUsage?.reduce<Record<string, any>>((acc, curr) => {
      acc[curr.date] = { date: curr.date, value: curr.totalCount };
      return acc;
    }, {});

    return {
      chartData:
        (dateArray.map((date) => {
          const data = dayMap?.[date];
          return data ? data : { date, value: 0 };
        }) as QueryDataPoint[]) || [],
      totalCount: Object.values(dayMap).reduce(
        (acc: number, curr) => acc + Number(curr.value),
        0,
      ),
    };
  }, [
    endDate,
    queryUsage.data?.performance.oldestQueryHistoryDate,
    queryUsage.data?.performance.resourceQueryHistory,
    setOldestDate,
    startDate,
  ]);

  const modelQueryHistoryState =
    queryUsage.data?.environment.modelQueryHistoryState.state;

  const noDataMessage =
    modelQueryHistoryState === ModelQueryHistoryStatus.Success
      ? 'No data available'
      : modelQueryHistoryState === ModelQueryHistoryStatus.NoData
        ? QUERY_USAGE_TOOLTIP_NO_DATA
        : QUERY_USAGE_TOOLTIP_ERROR;

  const xAccessor: Accessor<QueryDataPoint, string> = (x: QueryDataPoint) => {
    const [, MM, DD] = x.date.split('-');
    return `${MM}-${DD}`;
  };
  const yAccessor = (d: QueryDataPoint) => d.value;
  const xScale = categoryScale({
    labels: chartData?.map(xAccessor) ?? [],
  });

  const yScale = linearScale({
    min: 0,
    max: max(chartData || [], (x: QueryDataPoint) => x.value) || 0,
  });

  return (
    <>
      <div className="ml-[10px]">
        <div className="font-sans text-sm font-light leading-6 text-fgDecorative">
          Consumption query total
        </div>
        <span className="text-2xl">{totalCount}</span>
      </div>
      <div className="h-[300px]" data-testid="model-query-usage-chart-wrapper">
        <ChartWrapper
          testId="model-query-usage-chart"
          numDataPoints={
            modelQueryHistoryState === ModelQueryHistoryStatus.Success
              ? chartData?.length
              : 0
          }
          isLoading={queryUsage.fetching}
          error={queryUsage.error?.message}
          height={300}
          margin={{ bottom: 25, left: 45, right: 5, top: 15 }}
          xAccessor={xAccessor}
          xScale={xScale}
          yAccessor={yAccessor}
          yScale={yScale}
          noDataMessage={noDataMessage}
          tooltip={({ interactionItem }: TooltipCallbackArgs<QueryDataPoint>) => {
            const item = interactionItem?.item;
            if (!item) return null;

            return (
              <div>
                <div>
                  {item.date} - {item.value} queries
                </div>
              </div>
            );
          }}
        >
          <LinearAxis axis="y" label="Consumption query count" />
          <CategoryAxis axis="x" label="Date" />
          <Plot
            data={
              modelQueryHistoryState === ModelQueryHistoryStatus.Success && chartData
                ? chartData
                : []
            }
            itemComponent={Bars}
            testId="model-query-usage-chart-plot"
            extraItemProps={{
              className:
                'fill-fgVizIndigo transition-all hover:fill-fgVizIndigoHover hover:cursor-pointer',
            }}
          />
        </ChartWrapper>
      </div>
    </>
  );
};

export const ModelQueryUsageBarChart = (props: MostQueryUsageBarChartProps) => {
  const { enableModelQueryHistory } = useDbtCloudProject();

  if (!enableModelQueryHistory) {
    return null;
  }

  return <ModelQueryUsageBarChartImpl {...props} />;
};
